import { QueryStatus, type RetryOptions } from "@reduxjs/toolkit/query"
import { AxiosError } from "axios"
import { api, TAGS } from "@/store/api"
import { fromRest } from "@/store/apis/worldMapAPI/movementAdapters"
import { applyPlanWrite } from "@/store/apis/worldMapAPI/movementMerge"
import { worldMapSocket } from "@/store/apis/worldMapAPI/worldMap.socket"
import {
  IWorldMap,
  IGetSectorsReq,
  IMapSector,
  IPlayerWorldState,
  ISectorFull,
  IGetSectorDetailsReq,
  IMovementPlan,
  IMovementStateResponse,
  IMoveRequest,
  MOVEMENT_MODE
} from "@/store/apis/worldMapAPI/worldMap.types"
import { setConnectionStatus } from "@/store/slices/realtime"

const ENTRY_RETRY_DELAYS_MS = [1000, 2000, 4000]

const leaveMeFailureToEntryGate = (): void => undefined
const leavePlanFailureToItsReaders = (): void => undefined
const leaveMoveDenialToCaller = (): void => undefined
const awaitTerminalFromSocket = (): void => undefined

export const worldMapAPI = api
  .enhanceEndpoints({
    addTagTypes: [
      TAGS.WORLD_MAP,
      TAGS.PLAYER_POSITION,
      TAGS.SECTOR_DETAILS,
      TAGS.MOVEMENT
    ]
  })
  .injectEndpoints({
    endpoints: (build) => ({
      getActiveMap: build.query<IWorldMap, void>({
        query: () => ({ url: "/world-map/maps/active", method: "GET" }),
        providesTags: [TAGS.WORLD_MAP]
      }),
      getSectors: build.query<IMapSector[], IGetSectorsReq>({
        query: ({ mapId, minX, maxX, minY, maxY }) => ({
          url: "/world-map/sectors",
          method: "GET",
          params: { mapId, minX, maxX, minY, maxY }
        }),
        providesTags: [TAGS.WORLD_MAP]
      }),
      getPlayerWorldState: build.query<IPlayerWorldState, void>({
        query: () => ({ url: "/world-map/me", method: "GET" }),
        providesTags: [TAGS.PLAYER_POSITION],
        transformResponse: (res: {
          mapId: string
          x: number
          y: number
          movementMode: MOVEMENT_MODE
          movement: IMovementStateResponse | null
        }): IPlayerWorldState => ({
          mapId: res.mapId,
          x: res.x,
          y: res.y,
          movementMode: res.movementMode,
          movement: res.movement ? fromRest(res.movement) : null
        }),
        async onQueryStarted(_arg, { dispatch, getState, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled
            if (!data.movement) return
            const current =
              worldMapAPI.endpoints.getMovementState.select(undefined)(
                getState()
              ).data ?? null
            dispatch(
              worldMapAPI.util.upsertQueryData(
                "getMovementState",
                undefined,
                applyPlanWrite(current, data.movement, "rest")
              )
            )
          } catch {
            leaveMeFailureToEntryGate()
          }
        },
        extraOptions: {
          backoff: async (attempt: number, _maxRetries: number) => {
            const delay = ENTRY_RETRY_DELAYS_MS[attempt - 1] ?? 4000
            await new Promise<void>((resolve) => setTimeout(resolve, delay))
          },
          retryCondition: (
            error: unknown,
            _args: unknown,
            extraArgs: { attempt: number }
          ): boolean => {
            if (extraArgs.attempt > ENTRY_RETRY_DELAYS_MS.length) return false
            const status = (error as AxiosError).response?.status
            return status === undefined || status >= 500
          }
        } satisfies RetryOptions
      }),
      getSectorDetails: build.query<ISectorFull, IGetSectorDetailsReq>({
        query: ({ mapId, x, y }) => ({
          url: `/world-map/sectors/${x}/${y}`,
          method: "GET",
          params: { mapId }
        }),
        providesTags: (_result, _error, { x, y }) => [
          { type: TAGS.SECTOR_DETAILS, id: `${x}:${y}` }
        ]
      }),
      getMovementState: build.query<IMovementPlan | null, void>({
        query: () => ({ url: "/world-map/movement", method: "GET" }),
        providesTags: [TAGS.MOVEMENT],
        transformResponse: (res: IMovementStateResponse | null) =>
          res ? fromRest(res) : null,
        async onQueryStarted(_arg, { dispatch, getState, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled
            const me =
              worldMapAPI.endpoints.getPlayerWorldState.select(undefined)(
                getState()
              )
            if (
              data === null &&
              me.data?.movement &&
              me.status !== QueryStatus.pending
            ) {
              dispatch(worldMapAPI.util.invalidateTags([TAGS.PLAYER_POSITION]))
            }
          } catch {
            leavePlanFailureToItsReaders()
          }
        },
        async onCacheEntryAdded(
          _arg,
          { cacheDataLoaded, updateCachedData, cacheEntryRemoved, dispatch }
        ) {
          await cacheDataLoaded
          worldMapSocket.setUpdateData((recipe) =>
            updateCachedData((draft) => recipe(draft ?? null) as never)
          )
          worldMapSocket.setRefetch(() => {
            dispatch(setConnectionStatus("connected"))
            dispatch(
              worldMapAPI.util.invalidateTags([
                TAGS.MOVEMENT,
                TAGS.PLAYER_POSITION
              ])
            )
          })
          worldMapSocket.setDispatch(dispatch)
          worldMapSocket.start()
          worldMapSocket.addSub()
          const unbind = worldMapSocket.bindMovementEvents()
          try {
            await cacheEntryRemoved
          } finally {
            unbind()
            worldMapSocket.setUpdateData(null)
            worldMapSocket.setRefetch(null)
            worldMapSocket.removeSub()
          }
        }
      }),
      startMovement: build.mutation<IMovementPlan | null, IMoveRequest>({
        query: (body) => ({
          url: "/world-map/movement",
          method: "POST",
          data: body
        }),
        transformResponse: (res: IMovementStateResponse | null) =>
          res ? fromRest(res) : null,
        async onQueryStarted(_arg, { dispatch, getState, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled
            const current =
              worldMapAPI.endpoints.getMovementState.select(undefined)(
                getState()
              ).data ?? null
            dispatch(
              worldMapAPI.util.upsertQueryData(
                "getMovementState",
                undefined,
                data ? applyPlanWrite(current, data, "rest") : null
              )
            )
          } catch {
            leaveMoveDenialToCaller()
          }
        }
      }),
      cancelMovement: build.mutation<IMovementPlan | null, void>({
        query: () => ({ url: "/world-map/movement/cancel", method: "POST" }),
        transformResponse: (res: IMovementStateResponse | null) =>
          res ? fromRest(res) : null,
        async onQueryStarted(_arg, { dispatch, getState, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled
            const current =
              worldMapAPI.endpoints.getMovementState.select(undefined)(
                getState()
              ).data ?? null
            dispatch(
              worldMapAPI.util.upsertQueryData(
                "getMovementState",
                undefined,
                data ? applyPlanWrite(current, data, "cancel") : null
              )
            )
            if (data === null) {
              dispatch(worldMapAPI.util.invalidateTags([TAGS.PLAYER_POSITION]))
            }
          } catch {
            awaitTerminalFromSocket()
          }
        }
      })
    })
  })

export const {
  useGetActiveMapQuery,
  useGetSectorsQuery,
  useGetPlayerWorldStateQuery,
  useGetSectorDetailsQuery,
  useGetMovementStateQuery,
  useStartMovementMutation,
  useCancelMovementMutation
} = worldMapAPI
